import { Outlet, useLocation } from "react-router";
import { type FC, useCallback, useMemo, useState } from "react";
import { Layout, Menu, MenuProps } from "antd";
import { Toast, Button } from "antd-mobile";
import {
  // CompassOutlined, // Navigator (beta - hidden from menu)
  DoubleLeftOutlined,
  DoubleRightOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  SettingOutlined,
  UserOutlined,
  SearchOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { FilterOutline, AddOutline } from "antd-mobile-icons";
import classNames from "classnames";
import { isHomeHealthExternalUser } from "../../constants/sso";

// Local imports
import styles from "./styles.module.scss";
import { Header as HeaderMobile } from "../../components/LayoutComponents/Header/index.mobile";
import { MobileLeftMenu } from "../../components/LayoutComponents/LeftMenu/index.mobile";
import { Footer } from "../../components/LayoutComponents/Footer/index.mobile";
import { HeaderWeb } from "../../components/LayoutComponents/HeaderWeb";
import { useHook } from "./useHook";
import VisitNotesSearchModal from "../../components/CustomComponents/VisitNotesSearchModal";
import {
  premiumTabs as premiumTabsOriginal,
  settingsTabs,
  tabs as tabsOriginal,
  tabsNP,
  tabsWeb as tabsWebOriginal,
  tabsWebNP,
} from "./tabs";
import SurveyScheduler from "../../components/CustomComponents/SurveyScheduler";
import {
  amplifyProvider,
  authController,
  circadiaEngineProvider,
  clinicController,
  localStorageProvider,
  notificationController,
  patientController,
  pushNotificationProvider,
  timeProvider,
  watchlistController,
  electronEngineProvider,
  summaryController,
  webIntercomProvider,
  searchController,
  homeHealthController,
  analyticsController,
  smsController,
  userController,
} from "../../AppController";
import { useAuth } from "../../hooks/auth";
import { useRouterHook } from "../../hooks/useRouterHook";
import { AppErrorBoundary } from "../../components/CustomComponents/PageErrorBoundry";
import { ErrorComponent } from "../../components/CustomComponents/PageErrorBoundry/ErrorComponent";
import { NetworkBar } from "../../components/CustomComponents/NetworkBar";
import { appNotification } from "../../util/appNotification";
import { useScrollTop } from "../../hooks/useScrollTop";
import { useScreenTimeMetrics } from "../../hooks/useScreenTimeMetrics";
import { routes } from "../../router/mobile/routes";
import { ScreenLoaderWhite } from "../../components/CustomComponents/ScreenLoaderWhite";
import { useFCMTokenUpdate } from "../../hooks/fcmTokenListener";
import { sendZapierTrigger } from "../../util/zapierMain";
import { isMobile, isTablet, isWeb } from "../../util/layout";
import { TMobileRouterPath, useAppNavigate } from "../../hooks/router";
import logo from "../../assets/logo.svg";
import { useIntercom } from "../../hooks/useIntercom";
import { SearchPatientModal } from "../../components/CustomComponents/SearchPatientModal";
import { useSearchHook } from "./useSearchHook";
import { useScreenTimeout } from "../../hooks/useScreenTimeout";
import { useSiderAutoCollapse } from "../../hooks/useSiderAutoCollapse";
import { useMobileTopBottomColor } from "../../hooks/useMobileTopBottomColor";
import { isPIN } from "../../util/pin";
import { trackWatchlistAdd } from "../../util/engagementTracking";
import { useVitalsAIMode } from "../../hooks/useVitalsAIMode";
import {
  MainHeaderOverrideProvider,
  useMainHeaderOverride,
} from "../../contexts/MainHeaderOverrideContext";

const { Sider, Content } = Layout;

const prefetchAmcharts = () => import("@amcharts/amcharts5");
if (typeof window !== "undefined") {
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => prefetchAmcharts(), { timeout: 4000 });
  } else {
    setTimeout(prefetchAmcharts, 2000);
  }
}

type MenuItem = Required<MenuProps>["items"][number];

const MainLayoutHeader: FC<{
  onSearchPatientModalOpen: () => void;
  location: ReturnType<typeof useLocation>;
  isHomeHealthExternal: boolean;
}> = ({ onSearchPatientModalOpen, location, isHomeHealthExternal }) => {
  const { headerOverride, hideHeaderContainer } = useMainHeaderOverride();
  const hasOverride = headerOverride !== null && headerOverride !== undefined;

  return (
    <div
      className={classNames(styles.headerContainer, {
        [styles.headerHidden]: isHomeHealthExternal || hideHeaderContainer,
      })}
    >
      {hasOverride ? (
        <>{headerOverride}</>
      ) : (
        <HeaderWeb
          onClickSearch={onSearchPatientModalOpen}
          hideFacilitySelector={
            location.pathname === routes.newVisit.path ||
            location.pathname === routes.visitNotes.path ||
            location.pathname === routes.visitCalendar.path ||
            location.pathname.startsWith("/visit-notes/note/") ||
            location.pathname.startsWith("/visit-notes/pdf-viewer")
          }
        />
      )}
    </div>
  );
};

let lastMainPath = "";

const createMenuItems = (
  items:
    | typeof tabsWebOriginal
    | typeof premiumTabsOriginal
    | typeof settingsTabs
    | typeof tabsWebNP,
  collapsed: boolean,
  setRouteActive: (route: string) => void
): MenuItem[] => {
  return items.map((item) => ({
    label: item.title,
    icon: item.icon,
    key: item.key,
    onClick: () => setRouteActive(item.key.replace(/\/:\w+\??/g, "")), // remove any optional params from route
    className: classNames(
      styles.menuItem,
      !collapsed && styles.menuItemCollapsed
    ),
  }));
};

const MobileLayout: FC = () => {
  // Check if user is in Vitals AI mode to skip PIN requirement
  const isVitalsAIMode = useVitalsAIMode();

  useMobileTopBottomColor({
    circadiaEngineProvider: circadiaEngineProvider,
  });
  useFCMTokenUpdate({
    electronEngineProvider: electronEngineProvider,
    pushNotificationProvider: pushNotificationProvider,
  });
  const { populateAppData } = useRouterHook({
    amplifyProvider: amplifyProvider,
    authController: authController,
    clinicController: clinicController,
    localStorageProvider: localStorageProvider,
    notificationController: notificationController,
    watchlistController: watchlistController,
    timeProvider: timeProvider,
    appNotification: appNotification,
    patientController: patientController,
    circadiaEngineProvider,
    summaryController,
    homeHealthController,
    isPIN,
    smsController: smsController,
    userController: userController,
  });
  const location = useLocation();

  const {
    isSearchPatientModalOpen,
    onSearchPatientModalOpen,
    onSearchPatientModalClose,

    searchValue,
    onChange,
    response,

    isInWatchlist,
    addToWatchlist,
    removeFromWatchlist,
    isAddingList,
  } = useSearchHook({
    searchController: searchController,
    analyticsController,
    watchlistController,
    appNotification,
    trackWatchlistAdd: trackWatchlistAdd,
  });
  const {
    menuOpen,
    user,
    userClinic,
    toggleMenu,
    onClickSearch,
    showHeaderBar,
    showFooter,
    onRightIconClick,
    uniquePageKey,
    confirmLogout,
    onCancel,
    onLogoutClick,
    setConfirmLogout,
    localBetaFeature,
    onLocalBetaFeature,
    footerTapEvent,
    topRightIcon,
    connectionStatus,
    showLoading,
    onPatientClick,
    refreshSummary,
    onSetupClick,
  } = useHook({
    analyticsController,
    location,
    clinicController: clinicController,
    populateAppData,
    summaryController,
    localStorageProvider,
  });
  let tabsWeb = tabsWebOriginal;
  let tabs = tabsOriginal;
  let premiumTabs = premiumTabsOriginal;
  if (userClinic?.ehr_id == 0) {
    tabsWeb = tabsWebOriginal.filter((tab) => tab.key !== routes.Insights.path);
    tabs = tabsOriginal.filter((tab) => tab.key !== routes.Insights.path);
    premiumTabs = premiumTabsOriginal.filter(
      (tab) => tab.key !== routes.vitalSync.path
    );
  }

  const {
    menuHover,
    collapsed,
    handleCollapse,
    handleMenuHover,
    onSiderItemClick,
  } = useSiderAutoCollapse();

  useScreenTimeMetrics({ analyticsController });

  // useSummary(10 * 60 * 1000);

  // Wrap isPIN to skip PIN for Vitals AI mode users
  const isPINWithVitalsAICheck = useCallback(
    () => isPIN(isVitalsAIMode),
    [isVitalsAIMode]
  );

  useScreenTimeout({
    localStorageProvider,
    location,
    refreshSummary,
    isPIN: isPINWithVitalsAICheck,
  });
  const { onLogout } = useAuth({
    authController: authController,
    localStorageProvider: localStorageProvider,
    pushNotificationProvider,
    populateAppData,
    analyticsController,
    appNotification,
    circadiaEngineProvider,
    sendZapierTrigger,
  });

  const pathname = location.pathname;
  const buttonIcon =
    pathname === routes.watchlist.path ? (
      <AddOutline className={`${styles.actionButtonIconPlus}`} />
    ) : (
      <FilterOutline className={styles.actionButtonIconFilter} />
    );

  useScrollTop();

  const navigate = useAppNavigate();
  const setRouteActive = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate]
  );

  // Check if user belongs to home_health_external group or logged in via Microsoft SSO
  const isHomeHealthExternal = useMemo(() => {
    return isHomeHealthExternalUser(user?.groups);
  }, [user?.groups]);

  // State for visit notes search modal
  const [visitSearchModalOpen, setVisitSearchModalOpen] = useState(false);

  const handleVisitSearchClick = () => {
    setVisitSearchModalOpen(true);
  };

  const handleVisitSearchSelect = (note: any) => {
    setVisitSearchModalOpen(false);
    navigate(`/visit-notes/note/${note.noteId}`);
  };

  const items = useMemo(
    () =>
      createMenuItems(
        user?.isNursePractitioner ? tabsWebNP : tabsWeb,
        collapsed,
        setRouteActive
      ),
    [tabsWeb, tabsWebNP, collapsed, setRouteActive]
  );

  const premiumItems = useMemo(() => {
    // Get user groups for access control
    const userGroups = user?.groups;

    // Some of the premium tabs may be restricted to certain user groups
    // If a tab has a 'groups' property, only include it if the user belongs to one of those groups
    const filteredTabs = premiumTabs.filter(
      ({ groups }) =>
        !groups || groups.some((group) => userGroups?.includes(group))
    );

    return createMenuItems(filteredTabs, collapsed, setRouteActive);
  }, [premiumTabs, collapsed, setRouteActive, user?.groups]);

  const handleLogout = useCallback(() => {
    setConfirmLogout(false);
    setTimeout(() => {
      onLogout();
    }, 100);
  }, [onLogout]);

  const nextLastMainPath = [
    ...tabsWeb,
    ...tabsWebNP,
    ...premiumTabs,
    ...settingsTabs,
  ]
    .reverse()
    .find(({ key }) => {
      // Compare the path against the current location using URLPattern for dynamic segments
      if (typeof window !== "undefined" && "URLPattern" in window) {
        const pattern = new (window as any).URLPattern(
          key,
          window.location.origin
        );
        return pattern.test(
          location.pathname as TMobileRouterPath,
          window.location.origin
        );
      }
      // Fallback: simple string comparison if URLPattern is not available
      return (location.pathname as TMobileRouterPath) === key;
    });

  if (nextLastMainPath) {
    lastMainPath = nextLastMainPath.key;
  }

  const { handleToggle } = useIntercom({
    webIntercomProvider: webIntercomProvider,
    analyticsController: analyticsController,
  });

  if (
    location.pathname.includes("summary") ||
    location.pathname.includes("chime") ||
    location.pathname.includes("patient-profile") ||
    location.pathname.includes("user-info") ||
    location.pathname.includes("bed-exit-settings") ||
    location.pathname.includes("np-scheduling")
  ) {
    return (
      <div className={styles.app}>
        <div className={styles.body}>
          <AppErrorBoundary
            FallbackComponent={({ error, resetErrorBoundary }) => (
              <ErrorComponent
                error={error}
                resetErrorBoundary={resetErrorBoundary}
              />
            )}
            resetKeys={[uniquePageKey]}
          >
            <SurveyScheduler />
            <Outlet />
          </AppErrorBoundary>
        </div>
      </div>
    );
  }

  let shouldAttachToTop = false;
  const isAndroidWebView =
    typeof window !== "undefined" &&
    /android/i.test(navigator.userAgent) &&
    (/\bwv\b/i.test(navigator.userAgent) || !!(window as any).AndroidAuth);
  if (
    (isWeb() || isTablet()) &&
    (location.pathname === routes.search.path ||
      location.pathname === routes.changeFacility.path)
  ) {
    // Prevent a -56px upward shift when running inside Android WebView.
    // (WebView sometimes reports a "desktop-ish" width which can mistakenly route to web/tablet layout.)
    shouldAttachToTop = !isAndroidWebView;
  }

  // For home health external users, always show sidebar (even on narrow screens)
  const showSidebarForHomeHealth = isHomeHealthExternal;

  // Determine if we should use sidebar navigation behavior (navigate on click) vs mobile behavior (toggle menu)
  const useSidebarNavigation =
    isWeb() || isTablet() || showSidebarForHomeHealth;

  return (
    <MainHeaderOverrideProvider>
      <div className={styles.app}>
        <NetworkBar connectionStatus={connectionStatus} toast={Toast} />
        {(!isMobile() || showSidebarForHomeHealth) && (
          <Layout>
            <Sider
              width={200}
              theme="light"
              collapsible
              collapsed={collapsed}
              onCollapse={handleCollapse}
              className={classNames(
                styles.sider,
                showSidebarForHomeHealth && styles.homeHealthSider
              )}
              onClick={() => {
                if (collapsed) {
                  onSiderItemClick();
                }
              }}
              id="sider"
              collapsedWidth={80}
              onMouseEnter={isWeb() ? () => handleMenuHover(true) : undefined}
              onMouseLeave={isWeb() ? () => handleMenuHover(false) : undefined}
              onTouchStart={!isWeb() ? () => handleMenuHover(true) : undefined}
              onTouchEnd={!isWeb() ? () => handleMenuHover(false) : undefined}
            >
              <div
                className={classNames(
                  collapsed
                    ? styles.logoContainerCollapsed
                    : styles.logoContainer
                )}
              >
                {menuHover && collapsed ? (
                  <DoubleRightOutlined
                    className={styles.icon}
                    onClick={() => handleCollapse(!collapsed)}
                  />
                ) : (
                  <>
                    <img alt="err" src={logo} width="24" />
                    {!collapsed && (
                      <DoubleLeftOutlined
                        className={styles.icon}
                        onClick={() => handleCollapse(!collapsed)}
                      />
                    )}
                  </>
                )}
              </div>
              <div className={styles.menuSection} style={{ height: "100%" }}>
                {isHomeHealthExternal ? (
                  <div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={[
                        {
                          label: "Search",
                          icon: (
                            <SearchOutlined className={styles.footerIcon} />
                          ),
                          key: "search-visits",
                          onClick: () => {
                            handleVisitSearchClick();
                            if (!useSidebarNavigation) {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                        // {
                        //   label: "Navigator",
                        //   icon: (
                        //     <CompassOutlined className={styles.footerIcon} />
                        //   ),
                        //   key: routes.compassusNavigator.path,
                        //   onClick: () => {
                        //     if (useSidebarNavigation) {
                        //       navigate(routes.compassusNavigator.path);
                        //     } else {
                        //       toggleMenu();
                        //     }
                        //   },
                        //   className: classNames(
                        //     styles.menuItem,
                        //     !collapsed && styles.menuItemCollapsed
                        //   ),
                        // },
                        {
                          label: "Visits",
                          icon: (
                            <span
                              role="img"
                              aria-label="audio-file"
                              className={styles.footerIcon}
                            >
                              <svg
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                width="1em"
                                height="1em"
                              >
                                <path d="M4.38262 3.58877C4.30832 3.58877 4.24753 3.64957 4.24753 3.72387V4.53446C4.24753 4.60876 4.30832 4.66956 4.38262 4.66956H10.8673C10.9416 4.66956 11.0024 4.60876 11.0024 4.53446V3.72387C11.0024 3.64957 10.9416 3.58877 10.8673 3.58877H4.38262ZM7.48988 6.02054H4.38262C4.30832 6.02054 4.24753 6.08133 4.24753 6.15564V6.96622C4.24753 7.04053 4.30832 7.10132 4.38262 7.10132H7.48988C7.56418 7.10132 7.62498 7.04053 7.62498 6.96622V6.15564C7.62498 6.08133 7.56418 6.02054 7.48988 6.02054ZM6.67929 13.7549H2.89655V1.86627H12.3534V7.27019C12.3534 7.3445 12.4142 7.40529 12.4885 7.40529H13.4342C13.5085 7.40529 13.5693 7.3445 13.5693 7.27019V1.19078C13.5693 0.891878 13.3278 0.650391 13.0289 0.650391H2.22106C1.92215 0.650391 1.68066 0.891878 1.68066 1.19078V14.4304C1.68066 14.7293 1.92215 14.9708 2.22106 14.9708H6.67929C6.7536 14.9708 6.81439 14.91 6.81439 14.8357V13.89C6.81439 13.8157 6.7536 13.7549 6.67929 13.7549Z" />
                                <path d="M8.58691 10.3203C8.65432 10.3203 8.70701 10.376 8.70703 10.4414C8.70703 11.5978 9.61508 12.5322 10.7324 12.5322C11.8497 12.5321 12.7578 11.5977 12.7578 10.4414C12.7578 10.3762 12.8098 10.3206 12.877 10.3203H13.3691C13.4363 10.3206 13.4883 10.3762 13.4883 10.4414C13.4883 11.8871 12.4395 13.0803 11.0811 13.2578V14.0186H12.249C12.3259 14.0186 12.3913 14.06 12.4355 14.1191C12.4798 14.1783 12.5059 14.2576 12.5059 14.3428V14.9316C12.5058 14.9609 12.4963 14.9894 12.4795 15.0117C12.4628 15.0338 12.4359 15.0526 12.4014 15.0527H9.06348C9.02866 15.0527 9.00114 15.034 8.98438 15.0117C8.96761 14.9894 8.95902 14.9609 8.95898 14.9316V14.3428C8.95898 14.2577 8.98423 14.1782 9.02832 14.1191C9.07254 14.06 9.13805 14.0186 9.21484 14.0186H10.3506V13.252C9.00796 13.0604 7.97656 11.8751 7.97656 10.4414C7.97659 10.376 8.02833 10.3204 8.0957 10.3203H8.58691ZM10.7324 7.09473C11.532 7.09483 12.1777 7.755 12.1777 8.56738V10.458C12.1777 11.2704 11.532 11.9306 10.7324 11.9307C9.93279 11.9307 9.28613 11.2705 9.28613 10.458V8.56738C9.28613 7.75494 9.93279 7.09473 10.7324 7.09473ZM10.7324 7.84375C10.3363 7.84375 10.0166 8.16936 10.0166 8.56738V10.458C10.0166 10.856 10.3363 11.1807 10.7324 11.1807C11.1285 11.1806 11.4482 10.856 11.4482 10.458V8.56738C11.4482 8.16942 11.1285 7.84385 10.7324 7.84375Z" />
                              </svg>
                            </span>
                          ),
                          key: routes.visitNotes.path,
                          onClick: () => {
                            if (useSidebarNavigation) {
                              navigate(routes.visitNotes.path);
                            } else {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                        {
                          label: "Calendar",
                          icon: (
                            <CalendarOutlined className={styles.footerIcon} />
                          ),
                          key: routes.visitCalendar.path,
                          onClick: () => {
                            if (useSidebarNavigation) {
                              navigate(routes.visitCalendar.path);
                            } else {
                              toggleMenu();
                            }
                          },
                          className: classNames(
                            styles.menuItem,
                            !collapsed && styles.menuItemCollapsed
                          ),
                        },
                      ]}
                      className={styles.menu}
                    />
                  </div>
                ) : (
                  <div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={items}
                      className={styles.menu}
                    />

                    <div className={styles.menuDivider}>
                      {!collapsed && (
                        <span className={styles.menuDividerText}>
                          AI Agents
                        </span>
                      )}
                      <span className={styles.menuDividerLine} />
                    </div>
                    <Menu
                      defaultSelectedKeys={[lastMainPath]}
                      selectedKeys={[lastMainPath]}
                      mode="inline"
                      items={premiumItems}
                      className={styles.menu}
                    />
                  </div>
                )}
                <span className={styles.settingsMenuDividerLine} />
                <Menu
                  defaultSelectedKeys={[lastMainPath]}
                  selectedKeys={[lastMainPath]}
                  className={classNames(styles.menu, styles.menuSupport)}
                  mode="inline"
                  items={[
                    {
                      label: "Settings",
                      icon: <SettingOutlined className={styles.footerIcon} />,
                      key: routes.settings.path,
                      onClick: () => {
                        if (useSidebarNavigation) {
                          navigate(routes.settings.path);
                        } else {
                          toggleMenu();
                        }
                      },
                      className: classNames(
                        styles.menuItem,
                        !collapsed && styles.menuItemCollapsed
                      ),
                    },
                    {
                      label: "Knowledge Hub",
                      icon: <ReadOutlined className={styles.footerIcon} />,
                      key: "knowledge-hub",
                      onClick: () => {
                        window.open(
                          isHomeHealthExternal
                            ? "https://help.circadia.health/compassus/en/collections/18520749-getting-started"
                            : "https://help.circadia.health",
                          "_blank"
                        );
                      },
                      className: classNames(
                        styles.menuItem,
                        !collapsed && styles.menuItemCollapsed
                      ),
                    },
                    ...(!isHomeHealthExternal
                      ? [
                          {
                            label: "Help",
                            icon: (
                              <QuestionCircleOutlined
                                className={styles.footerIcon}
                              />
                            ),
                            key: "help-docs",
                            onClick: () => {
                              handleToggle();
                            },
                            className: classNames(
                              styles.menuItem,
                              !collapsed && styles.menuItemCollapsed
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            </Sider>
            <Layout
              className={collapsed ? "" : "sidebar-expanded"}
              style={
                {
                  "--sidebar-width": collapsed ? "80px" : "200px",
                } as React.CSSProperties
              }
            >
              <Content
                className={classNames(styles.content, {
                  [styles.attachedTop]: shouldAttachToTop,
                })}
              >
                <div id="body">
                  <MainLayoutHeader
                    onSearchPatientModalOpen={onSearchPatientModalOpen}
                    location={location}
                    isHomeHealthExternal={isHomeHealthExternal}
                  />
                  <AppErrorBoundary
                    FallbackComponent={({ error, resetErrorBoundary }) => (
                      <ErrorComponent
                        error={error}
                        resetErrorBoundary={resetErrorBoundary}
                        connectionStatus={connectionStatus}
                      />
                    )}
                    resetKeys={[uniquePageKey]}
                  >
                    {showLoading ? (
                      <ScreenLoaderWhite />
                    ) : (
                      <>
                        <SurveyScheduler />
                        <Outlet />
                      </>
                    )}
                  </AppErrorBoundary>
                </div>
              </Content>
            </Layout>
          </Layout>
        )}
        {isMobile() && !showSidebarForHomeHealth && (
          <>
            <div className={styles.body} id="body">
              {showHeaderBar() && !isHomeHealthExternal && (
                <HeaderMobile
                  onClickSearch={onClickSearch}
                  rightIcon={
                    topRightIcon !== undefined &&
                    false && (
                      <div className="headerRightButtonCon">
                        <Button
                          fill="none"
                          onClick={onRightIconClick}
                          data-test-id={
                            pathname === routes.watchlist.path
                              ? "button-add"
                              : "button-filter"
                          }
                        >
                          {buttonIcon}
                        </Button>
                      </div>
                    )
                  }
                  leftIcon={
                    <span id="menu-icon">
                      {/* <MenuOutlined className="searchIcon" onClick={toggleMenu} /> */}
                      <UserOutlined
                        className="searchIcon"
                        onClick={toggleMenu}
                      />
                    </span>
                  }
                />
              )}
              <AppErrorBoundary
                FallbackComponent={({ error, resetErrorBoundary }) => (
                  <ErrorComponent
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    connectionStatus={connectionStatus}
                  />
                )}
                resetKeys={[uniquePageKey]}
              >
                {showLoading ? (
                  <ScreenLoaderWhite />
                ) : (
                  <>
                    <SurveyScheduler />
                    <Outlet />
                  </>
                )}
              </AppErrorBoundary>
            </div>
            {showFooter() && (
              <div className={styles.bottom}>
                <Footer
                  tabs={user?.isNursePractitioner ? tabsNP : tabs}
                  tapEvent={footerTapEvent}
                />
              </div>
            )}
          </>
        )}

        <MobileLeftMenu
          user={user}
          userClinic={userClinic}
          open={menuOpen}
          onToggle={toggleMenu}
          onLogout={onLogoutClick}
          confirmLogout={confirmLogout}
          onCancel={onCancel}
          onConfirmLogout={handleLogout}
          analyticsController={analyticsController}
          circadiaEngineProvider={circadiaEngineProvider}
          localBetaFeature={localBetaFeature}
          onLocalBetaFeature={onLocalBetaFeature}
        />
        <SearchPatientModal
          isOpen={isSearchPatientModalOpen}
          onClose={onSearchPatientModalClose}
          searchValue={searchValue}
          onChange={onChange}
          response={response}
          isInWatchlist={isInWatchlist}
          addToWatchlist={addToWatchlist}
          removeFromWatchlist={removeFromWatchlist}
          isAddingList={isAddingList}
          onPatientClick={onPatientClick}
          onSetupClick={onSetupClick}
        />

        {/* Visit Notes Search Modal */}
        <VisitNotesSearchModal
          open={visitSearchModalOpen}
          onClose={() => setVisitSearchModalOpen(false)}
          onSelectNote={handleVisitSearchSelect}
        />
      </div>
    </MainHeaderOverrideProvider>
  );
};

export default MobileLayout;
